// src/components/About.jsx

import React, { useRef, useState } from "react";
import {
  motion,
  useScroll,
  useTransform,
} from "framer-motion";

import { FiVolume2, FiVolumeX } from "react-icons/fi";
import { useTranslation } from "react-i18next";

import fondo from "../assets/fondo1.jpg";
import videoAbout from "../assets/videoabout.mp4";

import ChatBot from "../components/home/ChatBot";

const Section = ({ children }) => {
  const ref = useRef(null);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 92%", "end 55%"],
  });

  const opacity = useTransform(
    scrollYProgress,
    [0, 1],
    [0.2, 1]
  );

  const y = useTransform(
    scrollYProgress,
    [0, 1],
    [40, 0]
  );

  const blur = useTransform(
    scrollYProgress,
    [0, 0.35],
    [8, 0]
  );

  const filter = useTransform(
    blur,
    (v) => `blur(${v}px)`
  );

  return (
    <motion.div
      ref={ref}
      style={{ opacity, y, filter }}
      className="will-change-transform"
    >
      {children}
    </motion.div>
  );
};

const About = () => {
  const { t } = useTranslation();

  const videoRef = useRef(null);

  const [muted, setMuted] = useState(true);

  // ================= SOUND =================

  const toggleSound = () => {
    const video = videoRef.current;

    if (!video) return;

    video.muted = !video.muted;

    setMuted(video.muted);
  };

  return (
    <div className="relative w-full overflow-hidden">

      {/* BACKGROUND */}
      <img
        src={fondo}
        alt="fondo"
        className="fixed inset-0 w-full h-full object-cover"
      />

      {/* OVERLAY */}
      <div className="fixed inset-0 bg-[#0E0B09]/90 backdrop-blur-sm" />

      {/* CONTENIDO */}
      <div
        className="
          relative z-10

          w-full

          max-w-[92%]
          sm:max-w-xl
          md:max-w-3xl
          lg:max-w-5xl

          mx-auto

          flex flex-col

          pt-32 sm:pt-36 md:pt-28
          pb-24
        "
      >

        {/* HEADER */}
        <Section>
          <div className="max-w-xl flex flex-col gap-5 md:gap-6">

            <h1
              className="
                uppercase
                text-[#C6A98A]
                font-light

                text-2xl
                tracking-[0.18em]

                sm:text-3xl
                sm:tracking-[0.22em]

                md:text-5xl
                md:tracking-[0.3em]
              "
            >
              {t("about.title")}
            </h1>

            <p
              className="
                text-[#E8D8C3]
                font-light
                leading-relaxed

                text-sm
                sm:text-base
                md:text-lg
              "
            >
              {t("about.intro")}
            </p>

          </div>
        </Section>

        {/* BLOQUE PRINCIPAL */}
        <div
          className="
            mt-14 md:mt-20

            grid
            grid-cols-1
            md:grid-cols-2

            gap-12 md:gap-20

            items-center
          "
        >

          {/* TEXTO */}
          <Section>
            <div className="flex flex-col gap-4 md:gap-5 max-w-md">

              <p
                className="
                  text-[#E8D8C3]

                  text-sm
                  sm:text-base
                  md:text-lg

                  leading-relaxed
                "
              >
                {t("about.text1")}
              </p>

              <p
                className="
                  text-[#BFA18A]
                  italic

                  border-l
                  border-[#3C2B22]

                  pl-4 py-1

                  text-sm
                  sm:text-base
                  md:text-lg
                "
              >
                {t("about.text2")}
              </p>

              <p
                className="
                  text-[#C6A98A]
                  uppercase

                  tracking-[0.3em]

                  text-[10px]
                  sm:text-xs

                  pt-2
                "
              >
                {t("about.keywords")}
              </p>

            </div>
          </Section>

          {/* VIDEO */}
          <Section>
            <div
              className="
                relative

                w-full
                h-[220px]
                sm:h-[280px]
                md:h-[380px]

                overflow-hidden
                rounded-2xl

                border border-[#3C2B22]/40

                shadow-2xl
                group
              "
            >

              <video
                ref={videoRef}
                className="
                  w-full
                  h-full
                  object-cover
                  cursor-pointer
                "
                src={videoAbout}
                autoPlay
                muted={muted}
                loop
                playsInline
                onClick={toggleSound}
              />

              {/* OVERLAY */}
              <div
                className="
                  absolute inset-0

                  bg-gradient-to-t
                  from-[#0E0B09]/50
                  to-transparent

                  pointer-events-none
                "
              />

              {/* BOTÓN SONIDO */}
              <button
                onClick={toggleSound}
                className="
                  absolute
                  bottom-4 right-4

                  bg-black/60
                  backdrop-blur-md

                  text-white

                  p-3
                  rounded-full

                  border border-white/20

                  hover:scale-110
                  active:scale-95

                  transition
                "
              >
                {muted
                  ? <FiVolumeX size={18} />
                  : <FiVolume2 size={18} />
                }
              </button>

              {/* HINT */}
              {muted && (
                <div
                  className="
                    absolute
                    bottom-4 left-4

                    text-xs
                    text-white/70

                    bg-black/40

                    px-3 py-1
                    rounded-full

                    backdrop-blur-sm
                  "
                >
                  {t("about.soundHint")}
                </div>
              )}

            </div>
          </Section>
        </div>

        {/* BLOQUE MEDIO */}
        <Section>
          <div
            className="
              mt-16 md:mt-24

              max-w-xl

              flex flex-col
              gap-4 md:gap-5
            "
          >

            <p
              className="
                text-[#E8D8C3]

                text-sm
                sm:text-base
                md:text-lg

                leading-relaxed
              "
            >
              {t("about.middle1")}
            </p>

            <p
              className="
                text-[#E8D8C3]

                text-sm
                sm:text-base
                md:text-lg

                leading-relaxed
              "
            >
              {t("about.middle2")}
            </p>

          </div>
        </Section>

        {/* FINAL */}
        <Section>
          <div
            className="
              mt-20 md:mt-28

              text-center

              max-w-md
              mx-auto

              flex flex-col
              gap-4
            "
          >

            <p
              className="
                text-[#BFA18A]
                italic

                text-sm
                sm:text-base
                md:text-lg

                leading-relaxed
              "
            >
              {t("about.final1")}
            </p>

            <p
              className="
                text-[#E8D8C3]

                text-sm
                sm:text-base
                md:text-lg
              "
            >
              {t("about.final2")}
            </p>

            <div className="w-20 h-px mx-auto bg-[#C6A98A]/40 mt-3" />

          </div>
        </Section>
      </div>

      <ChatBot />
    </div>
  );
};

export default About;